'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logClientEvent } from '@/lib/log-client-event';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Centered "account required" modal for guests who click a gated document
 * button: says a free account is needed, then sends them to Auth0 — on the
 * sign-up form ("Kostenloses Konto erstellen") or the login form ("Anmelden").
 * Both clicks are tracked as signup clicks with `source` (admin logs).
 * Controlled, so each caller owns its own `open` state.
 */
export function SignupRequiredDialog({
  open,
  onOpenChange,
  pathname,
  source,
  companyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pathname: string;
  source: string;
  companyId?: string;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (open) logClientEvent('paywall_shown', undefined, { kind: 'account', feature: source });
  }, [open, source]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('companyDetail.signupRequiredDialog.title', 'Kostenloses Konto erforderlich')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'companyDetail.signupRequiredDialog.description',
              'Um Dokumente anzusehen und herunterzuladen, benötigen Sie ein kostenloses FirmenData-Konto. Die Registrierung dauert weniger als eine Minute.',
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {/* rounded-full: same pill shape as the header's Register/Login buttons. */}
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => trackSignupAndRedirect(pathname, source, companyId, 'login')}
          >
            {t('companyDetail.signupRequiredDialog.login', 'Anmelden')}
          </Button>
          <Button
            className="rounded-full"
            onClick={() => trackSignupAndRedirect(pathname, source, companyId, 'signup')}
          >
            {t('companyDetail.signupRequiredDialog.cta', 'Kostenloses Konto erstellen')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
